import React, { useRef } from "react";
import html2canvas from "html2canvas-pro";
import { sleep } from "creso-core/utils";

export function CalculatorSnapshotContainer(props: {
  resultRender: (props: { onSnapshot: () => Promise<void> }) => React.ReactNode;
  inputRender: () => React.ReactNode;
  brandRender: () => React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  async function handleSnapshot() {
    if (!containerRef.current) return;

    const styles = getComputedStyle(document.documentElement);
    const cardColor = styles.getPropertyValue("--color-card");

    // Read all CSS rules already loaded in the browser — no network requests.
    // Fetching by <link href> would fail after a new deployment rotates the
    // content hash, leaving html2canvas's iframe with no Tailwind/CSS-variable
    // rules and a broken layout. Cross-origin sheets (e.g. Google Fonts CDN)
    // block cssRules access; fonts are handled separately below.
    const loadedCSS = Array.from(document.styleSheets)
      .flatMap((sheet) => {
        try {
          return Array.from(sheet.cssRules).map((rule) => rule.cssText);
        } catch {
          return [];
        }
      })
      .join("\n");

    await document.fonts.ready;

    const canvas = await html2canvas(containerRef.current, {
      scrollY: -window.scrollY,
      backgroundColor: cardColor,
      scale: window.devicePixelRatio * 2,
      useCORS: true,
      allowTaint: true,
      logging: true,
      onclone: (clonedDoc) => {
        // Inject all in-memory CSS as a single inline <style> so html2canvas's
        // iframe has every rule (Tailwind, CSS variables, @font-face) without
        // making any network requests.
        const style = clonedDoc.createElement("style");
        style.textContent = loadedCSS;
        clonedDoc.head.appendChild(style);

        // Needs to explicitly set font-family to fix letter spacing issues.
        const resetStyle = clonedDoc.createElement("style");
        resetStyle.textContent =
          "* { font-family: Inter, Helvetica, sans-serif !important; letter-spacing: 0 !important; }";
        clonedDoc.head.appendChild(resetStyle);

        // Inject already-loaded FontFace objects so the browser serves them
        // instantly if html2canvas tries to load them again (issue #1940).
        const fontPromises = Promise.race([
          sleep(3000),
          Promise.allSettled(
            Array.from(document.fonts).map((f) =>
              f.loaded.then((f) =>
                f.status === "loaded" ? clonedDoc.fonts.add(f) : null
              )
            )
          ),
        ]);

        return Promise.allSettled([
          sleep(2000),
          fontPromises,
          clonedDoc.fonts.ready,
        ]);
      },
    });

    const link = document.createElement("a");
    link.download = "calculator-result.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  }

  return (
    <>
      <div className="flex flex-col flex-1 rounded-lg w-full bg-card">
        <div className="flex flex-row bg-inherit gap-9 rounded-xl flex-1">
          {props.resultRender({ onSnapshot: handleSnapshot })}
        </div>
      </div>
      <div
        ref={containerRef}
        style={{
          position: "fixed",
          top: "-8888px",
          left: "-8888px",
          padding: "16px",
          width: "fit-content",
          minWidth: "800px",
        }}
        className="flex flex-col flex-1 rounded-lg w-full bg-card"
      >
        <div className="flex flex-row bg-inherit gap-9 rounded-xl flex-1">
          {props.inputRender()}
          {props.resultRender({ onSnapshot: handleSnapshot })}
        </div>
        {props.brandRender()}
      </div>
    </>
  );
}
